import { Component, OnInit } from '@angular/core';
import { NgIf, NgFor } from '@angular/common';
import { IonicModule, ViewWillEnter } from '@ionic/angular';
import { ActivatedRoute, RouterLink } from '@angular/router';
import { take } from 'rxjs/operators';
import { WindowSizes } from 'src/app/core/enums/window-sizes.enum';
import { GroupInterface } from 'src/app/core/interfaces/group.interface';
import { ReportConfig } from 'src/app/core/interfaces/report-config';
import { CollapsableTitleInterface, EmbedConfigModel } from 'src/app/core/interfaces/Collapsable-title';
import { ReportCardComponent } from 'src/app/shared/components/report-card/report-card.component';
import { EmbedDashboardCardComponent } from 'src/app/shared/components/embed-dashboard-card/embed-dashboard-card.component';
import { TranslateModule } from '@ngx-translate/core';
import { AuthService } from 'src/app/core/services/auth.service';
import { GroupsStore } from 'src/app/core/stores/groups.store';
import { CategoriesStore } from 'src/app/core/stores/categories.store';
import { ResourceService } from 'src/app/core/services/resource.service';
import { ResourcesEndpointEnum } from 'src/app/core/enums/resources-endpoint.enum';

export interface DashboardEmbed {
  embed: EmbedConfigModel;
  collapsableTitle: string;
  collapsableTitleId: number;
}

export interface DashboardReport {
  report: ReportConfig;
  groupName: string;
  collapsableTitle?: string;
}

@Component({
  selector: 'app-dashboard',
  templateUrl: './dashboard.component.html',
  styleUrls: ['./dashboard.component.scss'],
  standalone: true,
  imports: [
    NgIf,
    IonicModule,
    NgFor,
    RouterLink,
    ReportCardComponent,
    EmbedDashboardCardComponent,
    TranslateModule
  ],
})
export class DashboardComponent implements OnInit, ViewWillEnter {
  groups: GroupInterface[] = [];
  showLoadingReports = true;
  showLoadingEmbeds = true;
  reports: DashboardReport[] = [];
  standardReports: DashboardReport[] = [];
  groupReports: DashboardReport[] = [];
  embeds: DashboardEmbed[] = [];
  filterTitle: string | null = null;
  public windowwidth = innerWidth;
  onmobileView = false;
  windowSizes = WindowSizes;

  constructor(
    private authService: AuthService,
    private groupsStore: GroupsStore,
    private categoriesStore: CategoriesStore,
    private route: ActivatedRoute,
    private resourceService: ResourceService,
  ) {}

  ngOnInit() {
    if (this.windowwidth < this.windowSizes.MaxMobileWidth) {
      this.onmobileView = true;
    }
  }

  ionViewWillEnter() {
    this.showLoadingReports = true;
    this.showLoadingEmbeds = true;
    this.filterTitle = this.route.snapshot.paramMap.get('title');
    if (this.groupsStore.myGroups !== null) {
      this.groups = this.groupsStore.myGroups;
      this.loadAllReports();
    } else {
      this.resourceService.get<GroupInterface[]>(ResourcesEndpointEnum.GET_ME_REPORTS)
        .pipe(take(1))
        .subscribe(groups => {
          this.groupsStore.myGroups = groups ?? [];
          this.groups = groups ?? [];
          this.loadAllReports();
        });
    }
    this.loadEmbeds();
  }

  private loadEmbeds(): void {
    const buildEmbeds = (categories: CollapsableTitleInterface[]) => {
      const published = categories.filter(c => c.Published);
      const all = published.reduce((acc: DashboardEmbed[], c: CollapsableTitleInterface) => {
        (c.EmbedConfig ?? []).forEach(e => acc.push({ embed: e, collapsableTitle: c.Title, collapsableTitleId: c.CollapsableTitleID }));
        return acc;
      }, []);
      this.embeds = this.filterTitle ? all.filter(e => e.collapsableTitle === this.filterTitle) : all;
      this.showLoadingEmbeds = false;
    };
    if (this.categoriesStore.categories !== null) {
      buildEmbeds(this.categoriesStore.categories);
      return;
    }
    this.resourceService.get<CollapsableTitleInterface[]>(ResourcesEndpointEnum.GET_CATEGORIES)
      .pipe(take(1))
      .subscribe(cats => {
        this.categoriesStore.categories = cats ?? [];
        buildEmbeds(cats ?? []);
      });
  }

  private loadAllReports() {
    const activeGroups = this.groups.filter(g => g.Active);
    if (activeGroups.length === 0) {
      this.reports = [];
      this.showLoadingReports = false;
      return;
    }

    const seenPerGroup = new Map<string, Set<number>>();
    const reports: DashboardReport[] = [];

    activeGroups.forEach(group => {
      if (!group.Reports?.length) return;

      const collapsableTitle = (group.Collapsable && group.CollapsableTitle)
        ? group.CollapsableTitle
        : undefined;

      if (!seenPerGroup.has(group.Name)) {
        seenPerGroup.set(group.Name, new Set<number>());
      }
      const seen = seenPerGroup.get(group.Name)!;

      group.Reports.forEach(r => {
        if (!r || seen.has(r.ReportID)) return;
        seen.add(r.ReportID);
        reports.push({ report: r, groupName: group.Name, collapsableTitle });
      });
    });

    this.reports = this.filterTitle
      ? reports.filter(r => r.collapsableTitle === this.filterTitle)
      : reports;
    this.standardReports = this.reports.filter(r => r.report.isAllReport);
    this.groupReports = this.reports.filter(r => !r.report.isAllReport);
    this.showLoadingReports = false;
  }
}

